"use client";
import axios from "axios";

import React, { FC, PropsWithChildren, useState } from "react";

import { SortCategoriesGetter, MarketInsightsSortType } from "../types";
import { saEvent } from "../utils";
import { useMarkets } from "../hooks/useMarkets";
import Cookies from "js-cookie";

interface IOwnProps {
  env: string;
}

const SortContext =
  React.createContext<ReturnType<typeof useSortContextValue> | null>(null);

export const INITIAL_SORT = {
  categoryId: "PERFORMANCE",
  subCategoryId: "PRICE_RETURN",
  subCategoryLabel: "Price Return",
  mode: "top",
  isAll: false,
  period: "Year to Date",
} as const;

export const SortContextProvider: FC<PropsWithChildren<IOwnProps>> = ({
  env,
  children,
}) => {
  const value = useSortContextValue({ env });
  return (
    <SortContext.Provider
      value={{
        ...value,
      }}
    >
      {children}
    </SortContext.Provider>
  );
};

const useOptimisticValues = ({ sort }: { sort: MarketInsightsSortType }) => {
  const [optimisticSort, setOptimisticSort] = React.useState(sort);
  const category = sort.categoryId;
  const subCategory = sort.subCategoryId;

  const isLoading =
    category !== optimisticSort.categoryId ||
    subCategory !== optimisticSort.subCategoryId;

  React.useEffect(() => {
    setOptimisticSort(sort);
  }, [sort]);

  return {
    optimisticSort,
    setOptimisticSort,
    isLoading,
  };
};

const useSortContextValue = ({ env }: { env: string }) => {
  const [sort, setSort] = useState<MarketInsightsSortType>(INITIAL_SORT);
  const [watchlistId, setWatchlistId] = useState<string>("");
  const [whatIfPortfolioId, setWhatIfPortfolioId] = useState<string>("");
  const [originalPortfolioId, setOriginalPortfolioId] = useState<string>("");
  const [watchlistName, setWatchlistName] = useState<string>("");
  const optimisticSort = useOptimisticValues({ sort });
  const { data } = useMarkets(env as any);
  const token = Cookies.get("auth_token");
  React.useEffect(() => {
    if (data && data.length > 0) {
      setDefaultMarket();
    }
  }, [data, token]);

  const setDefaultMarket = () => {
    if (data && data.length > 0) {
      const defaultMarket = data.find((market) => market.default) || data[0];
      if (!defaultMarket) return;
      setWatchlist(
        defaultMarket.id,
        defaultMarket.title,
        defaultMarket.portfolioId,
        defaultMarket.originalPortfolioId,
      );
    }
  };
  const setAllSort = () => {
    saEvent("table-category-change", {
      selectedCategory: "All",
    });
    setSort({ ...INITIAL_SORT, isAll: true });
  };

  const resetSort = () => {
    setSort(INITIAL_SORT);
  };
  const setSortFake = (newSort: any) => {
    setSort(newSort);
  };

  const setWatchlist = (
    watchlist: string,
    name: string,
    portfolioId?: string,
    originalPortfolioId?: string,
  ) => {
    setWatchlistId(watchlist);
    setWatchlistName(name);
    if (portfolioId && originalPortfolioId) {
      setWhatIfPortfolioId(portfolioId);
      setOriginalPortfolioId(originalPortfolioId);
    } else {
      const actualPortfolioId = data?.find(
        (market) => market.id === watchlist,
      )?.portfolioId;
      const actualOriginalPortfolioId = data?.find(
        (market) => market.id === watchlist,
      )?.originalPortfolioId;
      if (actualPortfolioId && actualOriginalPortfolioId) {
        setWhatIfPortfolioId(actualPortfolioId);
        setOriginalPortfolioId(actualOriginalPortfolioId);
      }
    }
  };

  const fakeSetOptimistic = (newSortOptimistic: any) => {
    optimisticSort.setOptimisticSort(newSortOptimistic);
  };
  const isAllSort = sort.isAll || false;

  return {
    sort,
    setSort: setSortFake,
    setOptimisticSort: fakeSetOptimistic,
    optimisticSort,
    setAllSort,
    isAllSort,
    watchlistId,
    originalPortfolioId,
    setWatchlist,
    watchlistName,
    resetSort,
    setDefaultMarket,
    whatIfPortfolioId,
  };
};

export const useSortContext = () => {
  const value = React.useContext(SortContext);
  if (!value) {
    throw new Error("SortContext is not initialized");
  }
  return value;
};
